import {
  useEffect,
  useRef,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Transition, type TransitionStatus } from "react-transition-group";
import styles from "./Overlay.module.css";

export type OverlayPlacement = "center" | "sheet-bottom" | "sheet-start";

export interface OverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: OverlayPlacement;
  label?: string;
  panelStyle?: CSSProperties;
}

const PLACEMENT_CLASS: Record<OverlayPlacement, string> = {
  center: styles.center,
  "sheet-bottom": styles.sheetBottom,
  "sheet-start": styles.sheetStart,
};

// Matches --motion-duration; how long Transition keeps the node mounted for
// the exit animation after `open` flips false.
const MOTION_MS = 175;

const isShown = (state: TransitionStatus) =>
  state === "entering" || state === "entered";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Overlay({
  open,
  onClose,
  children,
  placement = "center",
  label,
  panelStyle,
}: OverlayProps): ReactElement {
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Scroll lock, focus handling and key traps stay tied to `open` so they
  // release the instant the overlay is dismissed, before the exit finishes.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    const raf = requestAnimationFrame(() => panelRef.current?.focus());

    function onKeyDown(event: KeyboardEvent) {
      const panel = panelRef.current;
      if (!panel) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = "";
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  return (
    <Transition
      in={open}
      timeout={MOTION_MS}
      nodeRef={backdropRef}
      appear
      mountOnEnter
      unmountOnExit
    >
      {(state) => {
        const shown = isShown(state);
        return createPortal(
          <div
            ref={backdropRef}
            className={`${styles.backdrop} ${shown ? styles.backdropOpen : ""}`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) onClose();
            }}
          >
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={label}
              tabIndex={-1}
              className={`${styles.panel} ${PLACEMENT_CLASS[placement]} ${
                shown ? styles.open : ""
              }`}
              style={panelStyle}
            >
              {children}
            </div>
          </div>,
          document.body,
        );
      }}
    </Transition>
  );
}
